import { io } from "socket.io-client";
import { backendUrl } from "../Router/constant";

// Visitor-mode chat — the same `/chat` namespace the training screen uses, but
// authenticated as an anonymous visitor rather than the account owner: a
// persisted visitorId and the page URL instead of a token and `mode: training`.
// Ported from the second half of test-training-chat.html.

const socketOrigin = () => String(backendUrl || "").replace(/\/+$/, "");

const visitorKey = (assistantId) => `visitorId_${assistantId}`;

/**
 * The visitor id is what lets the server recognise a returning visitor and
 * hand back their previous conversation, so it's kept in localStorage per
 * assistant rather than regenerated each load.
 */
export const getOrCreateVisitorId = (assistantId) => {
  const key = visitorKey(assistantId);

  try {
    let id = localStorage.getItem(key);

    if (!id) {
      id =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `v_${Date.now()}_${Math.random().toString(16).slice(2)}`;
      localStorage.setItem(key, id);
    }

    return id;
  } catch {
    // Private mode or blocked storage: a per-session id still works, the
    // visitor just won't be recognised on the next load.
    return `v_${Date.now()}`;
  }
};

/** Forgets the visitor, so the next connect looks like a brand-new person. */
export const resetVisitorId = (assistantId) => {
  try {
    localStorage.removeItem(visitorKey(assistantId));
  } catch {
    // Nothing stored to clear.
  }
};

/**
 * Opens a visitor connection.
 *
 * @param {string} assistantId
 * @param {{ visitorId: string, pageUrl?: string }} identity
 * @param {object} handlers
 */
export const connectVisitor = (assistantId, identity, handlers = {}) => {
  const socket = io(`${socketOrigin()}/chat`, {
    query: { assistantId },
    auth: {
      visitorId: identity.visitorId,
      pageUrl: identity.pageUrl || window.location.href,
    },
    transports: ["websocket", "polling"],
  });

  socket.on("connect", () => {
    socket.emit("start_conversation", {});
    handlers.onConnect?.();
  });

  socket.on("conversation_started", (data) => handlers.onStarted?.(data || {}));
  socket.on("conversation_created", (data) => handlers.onCreated?.(data || {}));
  socket.on("stage", (data) => handlers.onStage?.(data || {}));
  socket.on("final", (data) => handlers.onFinal?.(data || {}));

  // `code` names the case: "assistant_paused" means the account or the
  // assistant was switched off while this chat was open, so there is nothing
  // to retry - the caller closes the chat rather than showing a hiccup.
  socket.on("error", (data) =>
    handlers.onError?.(
      data?.message || "Something went wrong.",
      false,
      data?.code || "",
    ),
  );
  socket.on("connect_error", (err) =>
    handlers.onError?.(err?.message || "Connection rejected.", true),
  );
  socket.on("disconnect", () => handlers.onDisconnect?.());

  return socket;
};

export const sendVisitorMessage = (
  socket,
  conversationId,
  message,
  currentPageUrl,
) => {
  if (!socket) return;

  socket.emit("message", {
    ...(conversationId ? { conversationId } : {}),
    message,
    ...(currentPageUrl ? { currentPageUrl } : {}),
  });
};
